





























import { computed, defineComponent, inject, onBeforeUnmount, ref } from "@vue/composition-api";
import {
  ContentPriceSubjectTypeEnum,
  InputContentPriceUpdate,
} from "@scrile/api-provider/dist/api/ContentPriceProvider";
import { ContentSet } from "@scrile/api-provider/dist/api/ContentSetProvider";
import { InputContentSubscriptionLevelUpdate } from "@scrile/api-provider/dist/api/ContentSubscriptionLevelProvider";
import { PaymentPackage, PaymentPackageEnum } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import { SubscriptionLevel } from "@scrile/api-provider/dist/api/SubscriptionLevelProvider";
import { ContentFeedTypeEnum } from "@scrile/api-provider/dist/projects/scrileConnect/ContentFeedProvider";
import { checkAuthorities } from "@scrile/tools/dist/lib/SecurityHelpers";
import useAuthUser from "@/hooks/useAuthUser";
import providers from "@/lib/api";
import emitter from "@/lib/emitter";
import { $t } from "@/lib/i18nHelpers";
import { formatMoneyString, getCostByCurrency, getMainCurrencyCode } from "@/lib/moneyHelpers";
import { FormValues } from "@/types";
import { EVENTS } from "@/const";
import BaseChip from "@/components/BaseChip";
import BaseIcon from "@/components/BaseIcon";
import CheckAuthorities from "@/components/CheckAuthorities";
import FormatMoney from "@/components/FormatMoney";
import PriceSetupView from "../../../../components/PriceSetupView";
import OptionItem from "../../components/OptionItem";

export default defineComponent({
  name: "OptionPrice",
  components: { BaseChip, BaseIcon, FormatMoney, CheckAuthorities, OptionItem, PriceSetupView },
  props: {
    postData: { type: Object as () => ContentSet, required: true },
  },
  setup(props) {
    const { authUserStrict, authorities } = useAuthUser();

    const priceList = ref<SubscriptionLevel[]>([]);
    const packageList = ref<PaymentPackage[]>([]);
    const values = ref<FormValues>({ levelId: "0", pricePackageId: "0" });

    // TODO: those data is no reactive! Be careful
    const userId = inject<string>("contentFeedUserId", "");
    const setUpdateCb = inject<(cb: () => Promise<void>) => void>("setUpdateCb", async () => {});
    const removeCb = inject<(cb: () => Promise<void>) => void>("removeCb", async () => {});

    const showCostMenu = computed<boolean>(() => props.postData.type !== ContentFeedTypeEnum.EVENT);

    const priceOneTimePackage = computed(() => {
      const oneTimePackage: PaymentPackage | null =
        packageList.value.find(i => i.id === values.value.pricePackageId) || null;
      return (oneTimePackage && getCostByCurrency(oneTimePackage)) || null;
    });
    const priceOneTime = computed(() => priceOneTimePackage.value?.cost ?? 0);
    const priceOneTimeCurrency = computed<string | undefined>(
      () => priceOneTimePackage.value?.currency ?? getMainCurrencyCode()
    );

    const priceRecurringPackage = computed(() => {
      const recurringLevel: SubscriptionLevel | null = priceList.value.find(i => i.id === values.value.levelId) || null;
      return (recurringLevel?.pricePackage && getCostByCurrency(recurringLevel.pricePackage)) || null;
    });
    const priceRecurring = computed(() => priceRecurringPackage.value?.cost ?? 0);
    const priceRecurringCurrency = computed<string | undefined>(
      () => priceRecurringPackage.value?.currency ?? getMainCurrencyCode()
    );

    const isFree = computed<boolean>(() => priceOneTime.value === 0 && priceRecurring.value === 0);

    const currentTitle = computed(() => {
      if (isFree.value) {
        return $t("Set post price");
      }
      const strings: string[] = [];
      if (priceRecurring.value > 0) {
        const costRecurringTime = formatMoneyString(priceRecurring.value, {
          currencyCode: priceRecurringCurrency.value,
        });
        strings.push($t("{0}/mo", [costRecurringTime]));
      }
      if (priceOneTime.value > 0) {
        const costOneTime = formatMoneyString(priceOneTime.value, { currencyCode: priceOneTimeCurrency.value });
        strings.push($t("{0} once", [costOneTime]));
      }
      return $t("Post price: {0}", [strings.join(" ")]);
    });
    const getTitle = (isOpen: boolean) => (isOpen ? $t("Set post price") : currentTitle.value);

    const requiredAuthorities = computed<string[]>(() => {
      const isAuthor = authUserStrict.value.id === props.postData.userId || false;
      return isAuthor
        ? ["updateMyContentPrice", "updateMyContentSubscriptionLevel"]
        : ["updateAnyContentPrice", "updateAnyContentSubscriptionLevel"];
    });

    const allowUpdate = computed(() => checkAuthorities(authorities.value, requiredAuthorities.value));

    const updatePriceData = async () => {
      let priceUpdate: InputContentPriceUpdate = {
        packageId: values.value.pricePackageId,
        subject: {
          subjectType: ContentPriceSubjectTypeEnum.CONTENT_SET,
          subjectId: props.postData.id,
        },
      };
      let levelUpdate: InputContentSubscriptionLevelUpdate = {
        levelId: values.value.levelId,
        subject: {
          subjectType: ContentPriceSubjectTypeEnum.CONTENT_SET,
          subjectId: props.postData.id,
        },
      };
      await Promise.all([
        providers.ContentPriceProvider.update({ data: priceUpdate }),
        providers.ContentSubscriptionLevelProvider.update({ data: levelUpdate }),
      ]);
    };
    allowUpdate.value && setUpdateCb(updatePriceData);
    onBeforeUnmount(() => {
      allowUpdate.value && removeCb(updatePriceData);
    });

    const sendPriceEvent = (eventName = EVENTS.CONTENT_FEED_PRICE_CHANGED) => {
      emitter.emit(eventName, {
        recurringLevel: priceList.value.find(i => i.id === values.value.levelId) || null,
        oneTimePackage: packageList.value.find(i => i.id === values.value.pricePackageId) || null,
      });
    };

    const onSetPriceValues = (levelId = "0", pricePackageId = "0", eventName = EVENTS.CONTENT_FEED_PRICE_CHANGED) => {
      if (values.value.levelId === levelId && values.value.pricePackageId === pricePackageId) return;
      const priceChanged = values.value.levelId !== levelId || values.value.pricePackageId !== pricePackageId;
      values.value = {
        levelId: levelId,
        pricePackageId: pricePackageId,
      };
      if (priceChanged) {
        sendPriceEvent(eventName);
      }
    };

    const showReset = computed<boolean>(() => values.value.pricePackageId !== "0" || values.value.levelId !== "0");
    const onReset = () => onSetPriceValues();
    const onInput = (newValues: FormValues) => onSetPriceValues(newValues.levelId, newValues.pricePackageId);

    const getData = async () => {
      Promise.all([
        providers.ContentPriceProvider.search({
          subjects: [
            {
              subjectType: ContentPriceSubjectTypeEnum.CONTENT_SET,
              subjectId: props.postData.id,
            },
          ],
        }).then(r => (r.find(i => i.subject.subjectId === props.postData.id) || { packageId: "0" }).packageId),
        providers.ContentSubscriptionLevelProvider.search({
          subjects: [
            {
              subjectType: ContentPriceSubjectTypeEnum.CONTENT_SET,
              subjectId: props.postData.id,
            },
          ],
        }).then(r => (r.find(i => i.subject.subjectId === props.postData.id) || { levelId: "0" }).levelId),
        providers.SubscriptionLevelProvider.find({ userIds: [userId] }).then(r => (priceList.value = r)),
        providers.PaymentPackageProvider.findAll({ type: [PaymentPackageEnum.FILESET] }).then(
          r => (packageList.value = r)
        ),
      ]).then(([pricePackageId, levelId]) => {
        onSetPriceValues(levelId, pricePackageId, EVENTS.CONTENT_FEED_PRICE_LOADED);
      });
    };
    getData();

    return {
      showReset,
      values,
      showCostMenu,
      isFree,
      requiredAuthorities,
      priceList,
      packageList,
      getData,
      onReset,
      getTitle,
      onInput,
    };
  },
});
